/********************************************************************
 *  Copyright 2025 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  Adobe permits you to use, modify, and distribute this
 * file in accordance with the terms of the Adobe license agreement
 * accompanying it.
 *******************************************************************/

import { FunctionComponent, VNode } from 'preact';
import { HTMLAttributes } from 'preact/compat';
import { VComponent, classes } from '@adobe-commerce/elsie/lib';
import { ProductItemCardSkeleton } from '@adobe-commerce/elsie/components/ProductItemCard/ProductItemCardSkeleton';
import '@adobe-commerce/elsie/components/ProductItemCard/ProductItemCard.css';

export interface ProductItemCardProps
  extends Omit<HTMLAttributes<HTMLDivElement>, 'loading'> {
  image?: VNode;
  titleNode?: VNode;
  price?: VNode;
  sku?: VNode;
  actionButton?: VNode;
  swatches?: VNode;
  initialized?: boolean;
}

export const ProductItemCard: FunctionComponent<ProductItemCardProps> = ({
  className,
  image,
  titleNode,
  price,
  sku,
  actionButton,
  swatches,
  initialized = false,
  ...props
}) => {
  if (!initialized) {
    return <ProductItemCardSkeleton />;
  }
  return (
    <div
      {...props}
      className={classes(['dropin-product-item-card', className])}
    >
      <div className="dropin-product-item-card__image-container">
        {image && (
          <VComponent
            node={image}
            className={classes(['dropin-product-item-card__image'])}
          />
        )}
      </div>
      <div className="dropin-product-item-card__content">
        {titleNode && (
          <VComponent
            node={titleNode}
            className={classes(['dropin-product-item-card__title'])}
          />
        )}
        {sku && (
          <VComponent
            node={sku}
            className={classes(['dropin-product-item-card__sku'])}
          />
        )}
        {price && (
          <div className="dropin-product-item-card__price">
            <VComponent
              node={price}
              className={classes(['dropin-product-item-card__price'])}
            />
          </div>
        )}
        {swatches && (
          <div className="dropin-product-item-card__swatches">
            <VComponent
              node={swatches}
              className={classes(['dropin-product-item-card__swatches'])}
            />
          </div>
        )}
        {actionButton && (
          <div className="dropin-product-item-card__action">
            <VComponent
              node={actionButton}
              className={classes(['dropin-product-item-card__action'])}
            />
          </div>
        )}
      </div>
    </div>
  );
};
